import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Slider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Tabs,
  Tab,
  CircularProgress,
  Snackbar,
  Alert as MuiAlert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import PrintIcon from '@mui/icons-material/Print';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import PrintingSettingsTab from '../../settings/PrintingSettingsTab';
import ReceiptSettingsModal from '../../settings/ReceiptSettingsModal';
import salesService from '../../../../../services/salesService';
import { useTranslation } from 'react-i18next';
import {
  loadCashDrawerPrinter,
  saveCashDrawerPrinter,
  CashDrawerPrinterInfo,
  CashDrawerPin,
} from '../../utils/cashDrawerStorage';

interface DefinedPrinter {
  id: string;
  name: string;
}

interface InventoryCategory {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
}

interface QuickSaleUISettingsModalProps {
  open: boolean;
  onClose: () => void;
  currentCardSize: number;
  currentFontSize: number;
  currentCategoryTabSize: number;
  currentImageSize?: number;
  currentControlPanelPosition?: 'left' | 'right';
  currentShowProductImages?: boolean;
  currentShowProductPrices?: boolean;
  currentCartColumnWidth?: number;
  currentShowAllCategory?: boolean;
  currentShowSearchBar?: boolean;
  currentCategoryLayout?: 'horizontal' | 'vertical';
  currentCategoryPosition?: 'left' | 'right';
  currentCategoryFontSize?: number;
  currentUseFiscalDeviceForCash?: boolean;
  onUseFiscalDeviceForCashChange?: (enabled: boolean) => void;
  onSaveUISettings: (newSettings: {
    cardSize: number;
    fontSize: number;
    categoryTabSize: number;
    imageSize: number;
    controlPanelPosition: 'left' | 'right';
    showProductImages?: boolean;
    showProductPrices?: boolean;
    cartColumnWidth?: number;
    showAllCategory?: boolean;
    showSearchBar?: boolean;
    categoryLayout?: 'horizontal' | 'vertical';
    categoryPosition?: 'left' | 'right';
    categoryFontSize?: number;
  }) => void;

  // Yazdırma ayarları için ek prop'lar
  screenId?: number | null;
  companyId?: string | null;
  screenSettings?: any;
  displayableCategories?: InventoryCategory[];
  isRestaurantMode?: boolean;
  onPrintingSettingsSaved?: () => void;
}

const QuickSaleUISettingsModal: React.FC<QuickSaleUISettingsModalProps> = ({
  open,
  onClose,
  currentCardSize = 100,
  currentFontSize = 100,
  currentCategoryTabSize = 100,
  currentImageSize = 100,
  currentControlPanelPosition = 'right',
  currentShowProductImages = false,
  currentShowProductPrices = false,
  currentCartColumnWidth = 5,
  currentShowAllCategory = true,
  currentShowSearchBar = true,
  currentCategoryLayout = 'horizontal',
  currentCategoryPosition = 'left',
  currentCategoryFontSize = 100,
  currentUseFiscalDeviceForCash = true,
  onUseFiscalDeviceForCashChange,
  onSaveUISettings,
  screenId,
  companyId,
  screenSettings,
  displayableCategories = [],
  isRestaurantMode = false,
  onPrintingSettingsSaved,
}) => {
  const { t } = useTranslation('salesSystem');
  const [activeTab, setActiveTab] = useState(0);

  // =========== GÖRÜNÜM SEKMESİ STATE'LERİ ===========
  const [cardSize, setCardSize] = useState<number>(currentCardSize);
  const [fontSize, setFontSize] = useState<number>(currentFontSize);
  const [categoryTabSize, setCategoryTabSize] = useState<number>(currentCategoryTabSize);
  const [imageSize, setImageSize] = useState<number>(currentImageSize);
  const [controlPanelPosition, setControlPanelPosition] = useState<'left' | 'right'>(currentControlPanelPosition);
  const [showProductImages, setShowProductImages] = useState<boolean>(currentShowProductImages);
  const [showProductPrices, setShowProductPrices] = useState<boolean>(currentShowProductPrices);
  const [cartColumnWidth, setCartColumnWidth] = useState<number>(currentCartColumnWidth);
  const [showAllCategory, setShowAllCategory] = useState<boolean>(currentShowAllCategory);
  const [showSearchBar, setShowSearchBar] = useState<boolean>(currentShowSearchBar);
  const [categoryLayout, setCategoryLayout] = useState<'horizontal' | 'vertical'>(currentCategoryLayout);
  const [categoryPosition, setCategoryPosition] = useState<'left' | 'right'>(currentCategoryPosition);
  const [categoryFontSize, setCategoryFontSize] = useState<number>(currentCategoryFontSize);
  const [useFiscalDeviceForCash, setUseFiscalDeviceForCash] = useState<boolean>(currentUseFiscalDeviceForCash);

  // =========== YAZDIRMA SEKMESİ STATE'LERİ ===========
  const [printingMode, setPrintingMode] = useState<'single' | 'category'>('single');
  const [defaultReceiptPrinter, setDefaultReceiptPrinter] = useState<DefinedPrinter | null>(null);
  const [definedPrinters, setDefinedPrinters] = useState<DefinedPrinter[]>([]);
  const [categoryPrinterAssignments, setCategoryPrinterAssignments] = useState<{ [categoryId: string]: string | null }>({});
  // ✅ YENİ: Kategori başına 2. yazıcı (opsiyonel) — fiş her iki yazıcıya da gider
  const [categoryPrinterAssignments2, setCategoryPrinterAssignments2] = useState<{ [categoryId: string]: string | null }>({});
  const [adisyonPrinter, setAdisyonPrinter] = useState<DefinedPrinter | null>(null);
  const [uncategorizedPrinter, setUncategorizedPrinter] = useState<DefinedPrinter | null>(null);
  const [useBothFiscalAndReceiptPrinter, setUseBothFiscalAndReceiptPrinter] = useState(false);
  const [cashDrawerPrinter, setCashDrawerPrinter] = useState<CashDrawerPrinterInfo | null>(null);
  const [cashDrawerTestLoading, setCashDrawerTestLoading] = useState(false);
  const [receiptSettings, setReceiptSettings] = useState<any>(null);

  const [localPrinters, setLocalPrinters] = useState<DefinedPrinter[]>([]);
  const [loadingLocalPrinters, setLoadingLocalPrinters] = useState(false);
  const [localPrintersError, setLocalPrintersError] = useState<string | null>(null);
  const [printTestLoading, setPrintTestLoading] = useState<string | null>(null);

  const [receiptSettingsModalOpen, setReceiptSettingsModalOpen] = useState(false);
  const [printingSaving, setPrintingSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Modal açıldığında görünüm state'lerini senkronla
  useEffect(() => {
    if (open) {
      setCardSize(currentCardSize);
      setFontSize(currentFontSize);
      setCategoryTabSize(currentCategoryTabSize);
      setImageSize(currentImageSize);
      setControlPanelPosition(currentControlPanelPosition);
      setShowProductImages(currentShowProductImages);
      setShowProductPrices(currentShowProductPrices);
      setCartColumnWidth(currentCartColumnWidth);
      setShowAllCategory(currentShowAllCategory);
      setShowSearchBar(currentShowSearchBar);
      setCategoryLayout(currentCategoryLayout);
      setCategoryPosition(currentCategoryPosition);
      setCategoryFontSize(currentCategoryFontSize);
      setUseFiscalDeviceForCash(currentUseFiscalDeviceForCash);
      setActiveTab(0);
    }
  }, [open, currentCardSize, currentFontSize, currentCategoryTabSize, currentImageSize, currentControlPanelPosition, currentShowProductImages, currentShowProductPrices, currentCartColumnWidth, currentShowAllCategory, currentShowSearchBar, currentCategoryLayout, currentCategoryPosition, currentCategoryFontSize, currentUseFiscalDeviceForCash]);

  // Modal açıldığında yazdırma ayarlarını screenSettings'ten (SQL) yükle.
  // ⚠️ cashDrawerPrinter HARİÇ: O ayar bilgisayara özel olduğu için sadece
  // localStorage'dan okunur (aşağıdaki ayrı useEffect).
  useEffect(() => {
    if (open && screenSettings) {
      setPrintingMode(screenSettings.printingMode || 'single');
      setDefaultReceiptPrinter(screenSettings.defaultReceiptPrinter || null);
      setDefinedPrinters(screenSettings.definedPrinters || []);
      setCategoryPrinterAssignments(screenSettings.categoryPrinterAssignments || {});
      setCategoryPrinterAssignments2((screenSettings as any).categoryPrinterAssignments2 || {});
      setAdisyonPrinter(screenSettings.adisyonPrinter || null);
      setUncategorizedPrinter(screenSettings.uncategorizedPrinter || null);
      setUseBothFiscalAndReceiptPrinter(screenSettings.useBothFiscalAndReceiptPrinter || false);
      setReceiptSettings(screenSettings.receiptDisplaySettings || null);
    }
  }, [open, screenSettings]);

  // 💰 Para çekmecesi tetik yazıcısı → sadece localStorage'dan oku
  useEffect(() => {
    if (open) {
      const saved = loadCashDrawerPrinter(companyId, screenId);
      console.log(
        `💰 [QuickSaleUISettingsModal] cashDrawerPrinter localStorage'dan yüklendi:`,
        saved?.name || '(YOK)'
      );
      setCashDrawerPrinter(saved);
    }
  }, [open, companyId, screenId]);

  // Yazıcı listesi yükleme
  const fetchLocalPrinters = useCallback(async () => {
    setLoadingLocalPrinters(true);
    setLocalPrintersError(null);
    try {
      const response = await fetch('http://localhost:3003/api/yazicilariGetir');
      if (!response.ok) throw new Error('Yazıcı listesi alınamadı');
      const data = await response.json();
      const printers: DefinedPrinter[] = (data || []).map((p: any) => ({
        id: p.id || p.name || p.Name,
        name: p.name || p.Name || p.id,
      }));
      setLocalPrinters(printers);
    } catch (err: any) {
      setLocalPrintersError(err.message || 'Yerel yazıcılara erişilemedi');
      setLocalPrinters([]);
    } finally {
      setLoadingLocalPrinters(false);
    }
  }, []);

  // Yazdırma sekmesi açıldığında yazıcıları yükle
  useEffect(() => {
    if (open && activeTab === 1) {
      fetchLocalPrinters();
    }
  }, [open, activeTab, fetchLocalPrinters]);

  // =========== GÖRÜNÜM KAYDETME ===========
  const handleSaveAppearance = () => {
    onSaveUISettings({
      cardSize,
      fontSize,
      categoryTabSize,
      imageSize,
      controlPanelPosition,
      showProductImages,
      showProductPrices,
      cartColumnWidth,
      showAllCategory,
      showSearchBar,
      categoryLayout,
      categoryPosition,
      categoryFontSize,
    });
    if (onUseFiscalDeviceForCashChange && useFiscalDeviceForCash !== currentUseFiscalDeviceForCash) {
      onUseFiscalDeviceForCashChange(useFiscalDeviceForCash);
    }
    onClose();
  };

  const hasAppearanceChanges =
    cardSize !== currentCardSize ||
    fontSize !== currentFontSize ||
    categoryTabSize !== currentCategoryTabSize ||
    imageSize !== currentImageSize ||
    controlPanelPosition !== currentControlPanelPosition ||
    showProductImages !== currentShowProductImages ||
    showProductPrices !== currentShowProductPrices ||
    cartColumnWidth !== currentCartColumnWidth ||
    showAllCategory !== currentShowAllCategory ||
    showSearchBar !== currentShowSearchBar ||
    categoryLayout !== currentCategoryLayout ||
    categoryPosition !== currentCategoryPosition ||
    categoryFontSize !== currentCategoryFontSize ||
    useFiscalDeviceForCash !== currentUseFiscalDeviceForCash;

  // =========== YAZDIRMA KAYDETME ===========
  // Not: cashDrawerPrinter BİLGİSAYARA ÖZEL olduğu için SQL'e YAZILMAZ,
  // sadece localStorage'a yazılır. Diğer yazdırma ayarları SQL'e gider.
  const handleSavePrintingSettings = useCallback(async () => {
    if (!screenId) {
      setSnackbar({ open: true, message: 'Ekran bilgisi bulunamadı', severity: 'error' });
      return;
    }
    setPrintingSaving(true);
    try {
      // 💰 Para çekmecesi tetik yazıcısı → sadece localStorage (bilgisayara özel)
      saveCashDrawerPrinter(companyId, screenId, cashDrawerPrinter);

      const printingSettingsObj = {
        printingMode,
        defaultReceiptPrinter: printingMode === 'single' ? defaultReceiptPrinter : null,
        definedPrinters: printingMode === 'category' ? definedPrinters : [],
        categoryPrinterAssignments: printingMode === 'category' ? categoryPrinterAssignments : {},
        categoryPrinterAssignments2: printingMode === 'category' ? categoryPrinterAssignments2 : {},
        receiptDisplaySettings: receiptSettings,
        adisyonPrinter: printingMode === 'category' ? adisyonPrinter : null,
        uncategorizedPrinter: printingMode === 'category' ? uncategorizedPrinter : null,
        useBothFiscalAndReceiptPrinter,
      };

      await salesService.updateQuickSaleScreen({
        Id: screenId,
        PrintingSettingsJson: JSON.stringify(printingSettingsObj),
      });

      setSnackbar({ open: true, message: 'Yazdırma ayarları kaydedildi', severity: 'success' });
      onPrintingSettingsSaved?.();
    } catch (err: any) {
      console.error('Yazdırma ayarları kaydedilemedi:', err);
      setSnackbar({ open: true, message: 'Yazdırma ayarları kaydedilemedi: ' + (err.message || ''), severity: 'error' });
    } finally {
      setPrintingSaving(false);
    }
  }, [screenId, companyId, printingMode, defaultReceiptPrinter, definedPrinters, categoryPrinterAssignments, categoryPrinterAssignments2, receiptSettings, adisyonPrinter, uncategorizedPrinter, useBothFiscalAndReceiptPrinter, cashDrawerPrinter, onPrintingSettingsSaved]);

  // =========== PARA ÇEKMECESİ TEST ===========
  // Pin secimi:
  //   - 'auto' -> backend hem pin 2 hem pin 5 komutunu sirayla gonderir;
  //               cekmece hangi pine bagliysa o cevap verir. Test asamasinda
  //               kullanicinin "hangisi calisiyor" tespiti icin pratik.
  //   - 'pin2' -> sadece XPrinter/Epson varsayilani gonderilir.
  //   - 'pin5' -> sadece Afanda/Aafnet/bazi Hisense varsayilani gonderilir.
  // Detayli yanit (hangi pin OK / NOK) hem console hem snackbar'a yansir.
  const handleTestCashDrawer = useCallback(async (printerName: string, pin: CashDrawerPin = 'auto') => {
    setCashDrawerTestLoading(true);
    const localServiceUrl = screenSettings?.localServiceUrl || 'http://localhost:3003';
    const url = `${localServiceUrl}/api/print/open-cash-drawer`;
    const startedAt = performance.now();
    console.log(
      `%c💰 [CashDrawer:TEST] → POST ${url}`,
      'color:#2e7d32;font-weight:bold;',
      { printerName, pin }
    );
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ printerName, pin }),
      });
      const data = await response.json().catch(() => ({ success: response.ok }));
      const elapsed = Math.round(performance.now() - startedAt);
      console.log(`💰 [CashDrawer:TEST] yanit (${elapsed}ms) status=${response.status}`, data);
      if (Array.isArray(data?.details) && data.details.length > 0) {
        // eslint-disable-next-line no-console
        console.table(data.details);
      }
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || `HTTP ${response.status}`);
      }
      const okPins = Array.isArray(data?.details)
        ? data.details.filter((d: any) => d?.ok).map((d: any) => d.pin).join(', ')
        : '';
      setSnackbar({
        open: true,
        message:
          `Tetik gönderildi → ${printerName} [pin=${pin}] (${elapsed}ms)` +
          (okPins ? ` — OK: ${okPins}` : ''),
        severity: 'success',
      });
    } catch (err: any) {
      const elapsed = Math.round(performance.now() - startedAt);
      console.error(`💰 [CashDrawer:TEST] ✗ Hata (${elapsed}ms):`, err);
      setSnackbar({
        open: true,
        message: `Çekmece tetiği başarısız [pin=${pin}]: ` + (err.message || 'Bilinmeyen hata'),
        severity: 'error',
      });
    } finally {
      setCashDrawerTestLoading(false);
    }
  }, [screenSettings]);

  // =========== YAZDIRMA HANDLER'LARI ===========
  const handlePrintingModeChange = useCallback((_e: React.ChangeEvent<HTMLInputElement>, newMode: string) => {
    setPrintingMode(newMode as 'single' | 'category');
  }, []);

  const handleSetDefaultPrinter = useCallback((printer: DefinedPrinter) => {
    setDefaultReceiptPrinter(printer);
  }, []);

  const handleRemoveDefaultPrinter = useCallback(() => {
    setDefaultReceiptPrinter(null);
  }, []);

  const handleCategoryPrinterChange = useCallback((categoryId: string, printerId: string | null) => {
    setCategoryPrinterAssignments(prev => ({ ...prev, [categoryId]: printerId }));
    if (printerId) {
      const printer = localPrinters.find(p => p.id === printerId);
      if (printer && !definedPrinters.some(dp => dp.id === printer.id)) {
        setDefinedPrinters(prev => [...prev, printer]);
      }
    }
  }, [localPrinters, definedPrinters]);

  // ✅ YENİ: Kategori başına 2. yazıcı seçimi
  const handleCategoryPrinterChange2 = useCallback((categoryId: string, printerId: string | null) => {
    setCategoryPrinterAssignments2(prev => ({ ...prev, [categoryId]: printerId }));
    if (printerId) {
      const printer = localPrinters.find(p => p.id === printerId);
      if (printer && !definedPrinters.some(dp => dp.id === printer.id)) {
        setDefinedPrinters(prev => [...prev, printer]);
      }
    }
  }, [localPrinters, definedPrinters]);

  const handleAdisyonPrinterChange = useCallback((printer: DefinedPrinter | null) => {
    setAdisyonPrinter(printer);
  }, []);

  const handleUncategorizedPrinterChange = useCallback((printer: DefinedPrinter | null) => {
    setUncategorizedPrinter(printer);
  }, []);

  const handlePrintTestPage = useCallback(async (printerId: string, _printerName: string) => {
    setPrintTestLoading(printerId);
    try {
      await fetch('http://localhost:3003/api/sinamaSayfasiYazdir', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ printerName: printerId }),
      });
    } catch (err) {
      console.error('Test sayfası yazdırılamadı:', err);
    } finally {
      setPrintTestLoading(null);
    }
  }, []);

  const handleReceiptSettingsSave = useCallback((settings: any) => {
    setReceiptSettings(settings);
    setReceiptSettingsModalOpen(false);
  }, []);

  const uniqueCategoryIds = displayableCategories.map(c => c.id);

  // Yazdırma sekmesi mevcut mu
  const hasPrintingTab = !!screenId;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2, bgcolor: 'primary.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 1 }} />
            <Typography variant="h6" component="span">{t('quickSaleUISettingsModal.title')}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {hasPrintingTab && (
          <Tabs
            value={activeTab}
            onChange={(_e, v) => setActiveTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab icon={<VisibilityIcon />} iconPosition="start" label={t('quickSaleUISettingsModal.tabs.appearance')} sx={{ textTransform: 'none', minHeight: 48 }} />
            <Tab icon={<PrintIcon />} iconPosition="start" label={t('quickSaleUISettingsModal.tabs.printing')} sx={{ textTransform: 'none', minHeight: 48 }} />
          </Tabs>
        )}

        <DialogContent dividers sx={{ p: 2, minHeight: 400 }}>
          {/* =========== TAB 0: GÖRÜNÜM =========== */}
          {activeTab === 0 && (
            <>
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                <Typography gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1.5 }}>
                  {t('quickSaleUISettingsModal.appearance.cartSectionSettings')}
                </Typography>
                <FormControlLabel
                  control={<Switch checked={showSearchBar} onChange={(e) => setShowSearchBar(e.target.checked)} color="primary" size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">{t('quickSaleUISettingsModal.appearance.showSearchArea')}</Typography>
                      <Typography variant="caption" color="text.secondary">{t('quickSaleUISettingsModal.appearance.showSearchAreaHint')}</Typography>
                    </Box>
                  }
                  sx={{ mb: 1.5, display: 'flex' }}
                />
                <Typography id="cart-column-width-slider-label" gutterBottom variant="body2" sx={{ mt: 1 }}>
                  {t('quickSaleUISettingsModal.appearance.cartWidth')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {t('quickSaleUISettingsModal.appearance.cartWidthHint')}
                </Typography>
                <Slider
                  aria-labelledby="cart-column-width-slider-label"
                  value={cartColumnWidth}
                  onChange={(_e: Event, v: number | number[]) => setCartColumnWidth(v as number)}
                  step={0.5}
                  marks={[{ value: 3, label: t('quickSaleUISettingsModal.appearance.narrow') }, { value: 5, label: t('quickSaleUISettingsModal.appearance.normal') }, { value: 7, label: t('quickSaleUISettingsModal.appearance.wide') }]}
                  min={3}
                  max={7}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${Math.round(value * 8.33)}%`}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography id="card-size-slider-label" gutterBottom variant="body2">{t('quickSaleUISettingsModal.appearance.productCardSize')}</Typography>
                <Slider aria-labelledby="card-size-slider-label" value={cardSize} onChange={(_e: Event, v: number | number[]) => setCardSize(v as number)} step={10} marks min={70} max={150} valueLabelDisplay="auto" size="small" />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography id="image-size-slider-label" gutterBottom variant="body2">{t('quickSaleUISettingsModal.appearance.imageSize')}</Typography>
                <Slider aria-labelledby="image-size-slider-label" value={imageSize} onChange={(_e: Event, v: number | number[]) => setImageSize(v as number)} step={10} marks min={50} max={150} valueLabelDisplay="auto" size="small" />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography id="font-size-slider-label" gutterBottom variant="body2">{t('quickSaleUISettingsModal.appearance.fontSize')}</Typography>
                <Slider aria-labelledby="font-size-slider-label" value={fontSize} onChange={(_e: Event, v: number | number[]) => setFontSize(v as number)} step={10} marks min={70} max={130} valueLabelDisplay="auto" size="small" />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography id="category-tab-size-slider-label" gutterBottom variant="body2">{t('quickSaleUISettingsModal.appearance.categoryTabSize')}</Typography>
                <Slider aria-labelledby="category-tab-size-slider-label" value={categoryTabSize} onChange={(_e: Event, v: number | number[]) => setCategoryTabSize(v as number)} step={10} marks min={80} max={140} valueLabelDisplay="auto" size="small" />
              </Box>

              <Box sx={{ mb: 2 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 500, color: 'text.primary', mb: 0.5, fontSize: '0.875rem' }}>{t('quickSaleUISettingsModal.appearance.numpadPosition')}</FormLabel>
                  <RadioGroup row value={controlPanelPosition} onChange={(e) => setControlPanelPosition(e.target.value as 'left' | 'right')}>
                    <FormControlLabel value="left" control={<Radio size="small" />} label={<Typography variant="body2">{t('quickSaleUISettingsModal.appearance.leftSide')}</Typography>} />
                    <FormControlLabel value="right" control={<Radio size="small" />} label={<Typography variant="body2">{t('quickSaleUISettingsModal.appearance.rightSide')}</Typography>} />
                  </RadioGroup>
                </FormControl>
              </Box>

              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                <Typography gutterBottom sx={{ fontWeight: 'bold', color: 'info.main', mb: 1.5 }}>{t('quickSaleUISettingsModal.appearance.categoryButtonSettings')}</Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography id="category-font-size-slider-label" gutterBottom variant="body2">{t('quickSaleUISettingsModal.appearance.categoryFontSize')}</Typography>
                  <Slider aria-labelledby="category-font-size-slider-label" value={categoryFontSize} onChange={(_e: Event, v: number | number[]) => setCategoryFontSize(v as number)} step={10} marks min={60} max={140} valueLabelDisplay="auto" size="small" />
                </Box>
                <FormControlLabel
                  control={<Switch checked={showAllCategory} onChange={(e) => setShowAllCategory(e.target.checked)} color="primary" size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">{t('quickSaleUISettingsModal.appearance.showAllCategory')}</Typography>
                      <Typography variant="caption" color="text.secondary">{t('quickSaleUISettingsModal.appearance.showAllCategoryHint')}</Typography>
                    </Box>
                  }
                  sx={{ mb: 1.5, display: 'flex' }}
                />
                <FormControl component="fieldset" sx={{ mb: 1.5 }}>
                  <FormLabel component="legend" sx={{ fontWeight: 500, color: 'text.primary', mb: 0.5, fontSize: '0.875rem' }}>{t('quickSaleUISettingsModal.appearance.categoryLayout')}</FormLabel>
                  <RadioGroup row value={categoryLayout} onChange={(e) => setCategoryLayout(e.target.value as 'horizontal' | 'vertical')}>
                    <FormControlLabel value="horizontal" control={<Radio size="small" />} label={<Typography variant="body2">{t('quickSaleUISettingsModal.appearance.horizontalTop')}</Typography>} />
                    <FormControlLabel value="vertical" control={<Radio size="small" />} label={<Typography variant="body2">{t('quickSaleUISettingsModal.appearance.verticalSide')}</Typography>} />
                  </RadioGroup>
                </FormControl>
                {categoryLayout === 'vertical' && (
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontWeight: 500, color: 'text.primary', mb: 0.5, fontSize: '0.875rem' }}>{t('quickSaleUISettingsModal.appearance.verticalPosition')}</FormLabel>
                    <RadioGroup row value={categoryPosition} onChange={(e) => setCategoryPosition(e.target.value as 'left' | 'right')}>
                      <FormControlLabel value="left" control={<Radio size="small" />} label={<Typography variant="body2">{t('quickSaleUISettingsModal.appearance.leftSide')}</Typography>} />
                      <FormControlLabel value="right" control={<Radio size="small" />} label={<Typography variant="body2">{t('quickSaleUISettingsModal.appearance.rightSide')}</Typography>} />
                    </RadioGroup>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>{t('quickSaleUISettingsModal.appearance.verticalHint')}</Typography>
                  </FormControl>
                )}
              </Box>

              <Box sx={{ mb: 1 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 500, color: 'text.primary', mb: 0.5, fontSize: '0.875rem' }}>{t('quickSaleUISettingsModal.appearance.productCardViewSettings')}</FormLabel>
                  <FormControlLabel control={<Switch checked={showProductImages} onChange={(e) => setShowProductImages(e.target.checked)} color="primary" size="small" />} label={<Typography variant="body2">{t('quickSaleUISettingsModal.appearance.showImages')}</Typography>} sx={{ mb: 0 }} />
                  <FormControlLabel control={<Switch checked={showProductPrices} onChange={(e) => setShowProductPrices(e.target.checked)} color="primary" size="small" />} label={<Typography variant="body2">{t('quickSaleUISettingsModal.appearance.showPrices')}</Typography>} />
                </FormControl>
              </Box>

              {onUseFiscalDeviceForCashChange && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <Switch checked={useFiscalDeviceForCash} onChange={(e) => setUseFiscalDeviceForCash(e.target.checked)} color={useFiscalDeviceForCash ? 'success' : 'default'} size="small" />
                </Box>
              )}
            </>
          )}

          {/* =========== TAB 1: YAZDIRMA =========== */}
          {activeTab === 1 && hasPrintingTab && (
            <PrintingSettingsTab
              printingMode={printingMode}
              defaultReceiptPrinter={defaultReceiptPrinter}
              definedPrinters={definedPrinters}
              categoryPrinterAssignments={categoryPrinterAssignments}
              categoryPrinterAssignments2={categoryPrinterAssignments2}
              adisyonPrinter={adisyonPrinter}
              onAdisyonPrinterChange={handleAdisyonPrinterChange}
              uncategorizedPrinter={uncategorizedPrinter}
              onUncategorizedPrinterChange={handleUncategorizedPrinterChange}
              useBothFiscalAndReceiptPrinter={useBothFiscalAndReceiptPrinter}
              onUseBothFiscalAndReceiptPrinterChange={setUseBothFiscalAndReceiptPrinter}
              isRestaurantMode={isRestaurantMode}
              uniqueCategoryIds={uniqueCategoryIds}
              categoriesToListForPrinting={displayableCategories}
              localPrinters={localPrinters}
              loadingLocalPrinters={loadingLocalPrinters}
              localPrintersError={localPrintersError}
              onPrintingModeChange={handlePrintingModeChange}
              onSetDefaultPrinter={handleSetDefaultPrinter}
              onRemoveDefaultPrinter={handleRemoveDefaultPrinter}
              onCategoryPrinterChange={handleCategoryPrinterChange}
              onCategoryPrinterChange2={handleCategoryPrinterChange2}
              onRefreshLocalPrinters={fetchLocalPrinters}
              onPrintTestPage={handlePrintTestPage}
              printTestLoading={printTestLoading}
              onOpenReceiptSettingsModal={() => setReceiptSettingsModalOpen(true)}
              receiptNumberEnabled={receiptSettings?.showReceiptNumber || false}
              cashDrawerPrinter={cashDrawerPrinter}
              onCashDrawerPrinterChange={setCashDrawerPrinter}
              onTestCashDrawer={handleTestCashDrawer}
              cashDrawerTestLoading={cashDrawerTestLoading}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1 }}>
            <Button onClick={onClose} color="inherit">{t('quickSaleUISettingsModal.common.cancel')}</Button>
          {activeTab === 0 && (
            <Button onClick={handleSaveAppearance} variant="contained" color="primary" disabled={!hasAppearanceChanges}>
              {t('quickSaleUISettingsModal.common.save')}
            </Button>
          )}
          {activeTab === 1 && hasPrintingTab && (
            <Button
              onClick={handleSavePrintingSettings}
              variant="contained"
              color="primary"
              disabled={printingSaving}
              startIcon={printingSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            >
              {printingSaving ? t('quickSaleUISettingsModal.common.saving') : t('quickSaleUISettingsModal.common.savePrintSettings')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Fiş Yazdırma Ayarları Modalı */}
      {receiptSettingsModalOpen && (
        <ReceiptSettingsModal
          visible={receiptSettingsModalOpen}
          onCancel={() => setReceiptSettingsModalOpen(false)}
          onSave={handleReceiptSettingsSave}
          initialSettings={receiptSettings || {
            companyName: '',
            companyAddress: '',
            taxNumber: '',
            showReceiptHeader: true,
            showVat: true,
            showDiscountInfo: true,
            showCashierName: false,
            showReceiptNumber: false,
            receiptNumberResetValue: 1,
            resetAtEndOfDay: false,
            printCopyCount: 1,
            productGrouping: 'none',
            standardFooterNote: '',
            thankYouMessage: 'Teşekkür ederiz!',
            receiptWidth: 80,
            characterLength: 48,
            marginTop: 0,
            marginBottom: 0,
            marginLeft: 0,
            marginRight: 0,
            headerType: 'text',
          }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default QuickSaleUISettingsModal;
